<template>
  <div class="dropdown share-dropdown" ref="dropdownRoot">
    <i
      ref="toggleBtn"
      class="fa-solid fa-share-nodes dropdown-toggle"
      :id="dropdownId"
      role="button"
      aria-haspopup="true"
      :aria-expanded="isShown.toString()"
      @click="onToggleClick"
    ></i>

    <ul
      ref="menu"
      class="dropdown-menu"
      :class="[
        { show: isShown },
        openLeft ? 'dropdown-menu-end' : 'dropdown-menu-start'
      ]"
      :aria-labelledby="dropdownId"
    >
      <li v-for="p in normalizedPlatforms" :key="p.key">
        <a
          href="#"
          class="dropdown-item d-flex align-items-center"
          @click.prevent="onShare(p.key)"
          :title="p.label"
        >
          <span
            class="platform-badge"
            :style="{ backgroundColor: badgeBg(p.key) }"
          >
            <i :class="p.icon" class="fa-fw"></i>
          </span>
          <span class="platform-label">{{ p.label }}</span>
        </a>
      </li>
      
      <li v-if="isNativeShareSupported">
        <a
          href="#"
          class="dropdown-item d-flex align-items-center"
          @click.prevent="onNativeShare"
          title="Share via device"
        >
          <span
            class="platform-badge"
            :style="{ backgroundColor: '#28a745' }"
          >
            <i class="fa-solid fa-share fa-fw"></i>
          </span>
          <span class="platform-label">More Options</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script>
export default {
  name: "SocialShareButton",

  props: {
    title: {
      type: String,
      default: "",
    },
    description: {
      type: String,
      default: "",
    },
    url: {
      type: String,
      default: "",
    },
    platforms: {
      type: Array,
      default: () => ["facebook", "linkedin", "x", "whatsapp", "email"],
    },
  },

  data() {
    return {
      isShown: false,
      dropdownId: `share_${Math.random().toString(36).slice(2, 8)}`,
      isCopied: false,

      platformMap: {
        facebook: { icon: "fa-brands fa-facebook-f", label: "Facebook" },
        linkedin: { icon: "fa-brands fa-linkedin-in", label: "LinkedIn" },
        x: { icon: "fa-brands fa-x-twitter", label: "X" },
        whatsapp: { icon: "fa-brands fa-whatsapp", label: "WhatsApp" },
        email: { icon: "fa-solid fa-envelope", label: "Email" },
      },

      brandColors: {
        facebook: "#1877F2",
        linkedin: "#0077B5",
        x: "#000000",
        whatsapp: "#25D366",
        email: "#6c757d",
      },
      openLeft: false,
    };
  },

  computed: {
    pageUrl() {
      const link = this.url || window.location.href;
      return /^https?:\/\//i.test(link) ? link : `https://${link}`;
    },

    normalizedPlatforms() {
      return this.platforms
        .filter((key) => this.platformMap[key])
        .map((key) => ({
          key,
          ...this.platformMap[key],
        }));
    },

    copyIcon() {
      return this.isCopied ? "fa-solid fa-check" : "fa-solid fa-link";
    },

    copyLinkText() {
      return this.isCopied ? "Copied!" : "Copy Link";
    },

    isNativeShareSupported() {
      return navigator.share !== undefined;
    },
  },

  methods: {
    onToggleClick() {
      this.isShown = !this.isShown;
      this.isCopied = false;
      if (this.isShown) {
        this.$nextTick(() => {
          const toggle = this.$refs.toggleBtn;
          const menu = this.$refs.menu;

          if (toggle && menu) {
            const toggleRect = toggle.getBoundingClientRect();
            const menuWidth = menu.offsetWidth;
            const menuHeight = menu.offsetHeight;

            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // ---------- HORIZONTAL ----------
            let left = toggleRect.right;
            if (left + menuWidth > viewportWidth) {
              left = toggleRect.left - menuWidth;
            }
            if (left < 8) left = 8;

            // ---------- VERTICAL ----------
            let top = toggleRect.top;
            if (top + menuHeight > viewportHeight) {
              top = viewportHeight - menuHeight - 8;
            }
            if (top < 8) top = 8;

            // Apply styles
            menu.style.position = "fixed";
            menu.style.left = `${left}px`;
            menu.style.top = `${top}px`;
            menu.style.zIndex = 1050;
          }

          document.addEventListener("click", this.handleOutsideClick);
        });
      }
    },
    handleOutsideClick(e) {
      if (!this.$refs.dropdownRoot.contains(e.target)) {
        this.isShown = false;
        this.isCopied = false;
        document.removeEventListener("click", this.handleOutsideClick);
      }
    },
    badgeBg(key) {
      return this.brandColors[key] || "#999";
    },
    async onCopyLink() {
      const url = this.pageUrl;

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(url);
          this.isCopied = true;

          setTimeout(() => {
            this.isCopied = false;
          }, 2000);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = url;
          textArea.style.position = "fixed";
          textArea.style.left = "-999999px";
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);

          this.isCopied = true;
          setTimeout(() => {
            this.isCopied = false;
          }, 2000);
        }
      } catch (err) {
        console.error("Failed to copy:", err);
        alert("Failed to copy link. Please copy manually: " + url);
      }
    },
    onNativeShare() {
      const url = this.pageUrl;
      const title = this.title || "";
      const description = this.description || "";

      if (navigator.share) {
        navigator
          .share({
            title: title || undefined,
            text: description || undefined,
            url: url,
          })
          .then(() => {
            this.isShown = false;
          })
          .catch((err) => {
            if (err.name !== "AbortError") {
              console.error("Native share failed:", err);
            }
          });
      }
    },
    onShare(platform) {
      const url = this.pageUrl;
      // const url = "https://dev.talentfind.io/ohr/campaign/7b45cb66-7c57-4509-a8ee-224704cb04e3";
      const title = this.title || "";
      const description = this.description || "";

      const encodedUrl = encodeURIComponent(url);

      let shareUrl = "";
      let target = "_blank";
      let features = "noopener,noreferrer,width=600,height=400";

      switch (platform) {
        case "facebook":
          shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
          break;

        case "linkedin":
          shareUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodedUrl}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(description)}`;
          break;

        case "x":
        case "twitter":
          const tweetText = [title, description]
            .filter(Boolean)
            .join(" - ");
          shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
            tweetText
          )}&url=${encodedUrl}`;
          break;

        case "whatsapp":
          const whatsappText = [title, description, url]
            .filter(Boolean)
            .join("\n\n");
          shareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(
            whatsappText
          )}`;
          break;

        case "email": {
          const subject = title || "Check this out";
          
          this.isShown = false;

          const bodyText = [description, "", url].filter(Boolean).join("\n");

          const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(
            subject
          )}&body=${encodeURIComponent(bodyText)}`;

          const emailWindow = window.open(gmailUrl, "_blank", "noopener,noreferrer,width=800,height=600");

          setTimeout(() => {
            if (!emailWindow || emailWindow.closed) {
              const outlookUrl = `https://outlook.live.com/mail/0/deeplink/compose?subject=${encodeURIComponent(
                subject
              )}&body=${encodeURIComponent(bodyText)}`;
              
              const outlookWindow = window.open(outlookUrl, "_blank");
              
              setTimeout(() => {
                if (!outlookWindow || outlookWindow.closed) {
                  window.location.href = `mailto:?subject=${encodeURIComponent(
                    subject
                  )}&body=${encodeURIComponent(bodyText)}`;
                }
              }, 500);
            }
          }, 1000);

          return;
        }
        default:
          window.open(url, "_blank", "noopener,noreferrer");
          this.isShown = false;
          return;
      }

      window.open(shareUrl, target, features);
      this.isShown = false;
    },
  },

  beforeDestroy() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
};
</script>

<style scoped>
.share-dropdown {
  display: inline-block;
  position: relative;
}

.dropdown-toggle {
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
  transition: color 0.2s;
}

.dropdown-toggle:hover {
  color: #495057;
}

.dropdown-toggle::after {
  display: none;
}

.dropdown-menu {
  min-width: 110px;
  box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.1);
  padding: 0.5rem 0;
  max-width: calc(100vw - 1rem);
}

.dropdown-item {
  padding: 0.65rem 1rem;
  transition: background-color 0.2s;
  gap: 0;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.platform-badge {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 0.95rem;
  margin-right: 12px;
}

.platform-label {
  font-size: 0.95rem;
  font-weight: 500;
  color: #212529;
  line-height: 1.4;
}

.dropdown-divider {
  margin: 0.5rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }

  to {
    opacity: 1;
    transform: scale(1);
  }
}

.platform-label {
  animation: fadeIn 0.2s ease;
}

.dropdown-item:hover .platform-badge i,
.dropdown-item .platform-badge i {
  color: #fff;
}

.share-dropdown .dropdown-menu {
  position: fixed !important;
  z-index: 1050;
}
</style>